import React from 'react'
import { Textarea } from '../textarea/textarea'
import { FormGroup } from '../form-group/form-group'
import { Label } from '../label/label'
import { IBaseProps } from '../base-props'
import { on } from 'process'
import _ from 'lodash'

export interface ITypeaheadItem {
  label: string
  description?: string
  insertText: string
}

export interface ITypeaheadTextareaProps extends IBaseProps {
  label: string
  labelProps?: any
  textareaProps?: any
  isHorizontalLayout?: boolean
  itemRenderer: (item: ITypeaheadItem) => React.ReactNode
  items: ITypeaheadItem[]
  triggerToken: string
  onChange?: (value: string) => void
  initialValue?: string
  value?: string
}

interface ITypeaheadTextareaState {
  inputValue: string
  typeaheadIsOpen: boolean
  typeaheadQuery: string
  typeaheadStart: number
  typeaheadSuggestionIndex: number

  typeaheadPositionX?: number
  typeaheadPositionY?: number
}

export class TypeaheadTextarea extends React.Component<
  ITypeaheadTextareaProps,
  ITypeaheadTextareaState
> {
  public static defaultProps: Partial<ITypeaheadTextareaProps> = {
    items: [],
    triggerToken: '/',
    itemRenderer: renderItem,
    isHorizontalLayout: true,
  }

  private textareaRef = React.createRef<Textarea>()
  private wrapperRef = React.createRef<HTMLDivElement>()
  private typeaheadRef = React.createRef<HTMLDivElement>()

  constructor(props: ITypeaheadTextareaProps) {
    super(props)
    const { value } = props
    this.state = {
      inputValue: value ?? '',
      typeaheadIsOpen: false,
      typeaheadQuery: '',
      typeaheadStart: -1,
      typeaheadSuggestionIndex: 0,

      typeaheadPositionX: undefined,
      typeaheadPositionY: undefined,
    }
  }

  componentDidUpdate(prevProps: ITypeaheadTextareaProps) {
    const { value } = this.props
    if (value !== prevProps.value && value !== undefined) {
      this.setState({ inputValue: value })
    }
  }

  public render() {
    const { items, isHorizontalLayout, label, textareaProps, labelProps, value, children } =
      this.props
    const { inputValue } = this.state
    return (
      <div
        ref={this.wrapperRef}
        onKeyDownCapture={this.handleWrapperKeyDownCapture}
        style={{ position: 'relative' }}
      >
        <FormGroup isHorizontalLayout={isHorizontalLayout}>
          <Label isHorizontalLayout={isHorizontalLayout} text={label} {...labelProps} />
          <div className="flex-column" style={{ flex: 1, position: 'relative' }}>
            <Textarea
              ref={this.textareaRef}
              onChange={this.handleChange}
              value={inputValue}
              style={{ paddingBottom: '25px' }}
              {...textareaProps}
            />
            {children}
          </div>
        </FormGroup>

        {this.renderTypeahead()}
      </div>
    )
  }

  private renderTypeahead() {
    const { items, itemRenderer } = this.props
    const { typeaheadIsOpen, typeaheadPositionX, typeaheadPositionY, typeaheadSuggestionIndex } =
      this.state
    return (
      <>
        {typeaheadIsOpen &&
          items.length > 0 &&
          _.isNumber(typeaheadPositionX) &&
          _.isNumber(typeaheadPositionY) && (
            <div
              role="listbox"
              ref={this.typeaheadRef}
              className="c-mte-typeahead"
              style={{
                left: Math.max(8, typeaheadPositionX),
                top: typeaheadPositionY,
              }}
            >
              <div className="c-mte-typeahead-header">
                <i>Press Esc to exit menu</i>
              </div>
              {items.map((v, i) => (
                <div
                  key={v.insertText}
                  data-active={i === typeaheadSuggestionIndex ? 'true' : undefined}
                  onClick={(ev) => {
                    ev.preventDefault()
                    this.handleItemSelected(v)
                  }}
                  role="option"
                  aria-selected={i === typeaheadSuggestionIndex}
                  style={{
                    display: 'grid',
                    gap: 2,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    background: i === typeaheadSuggestionIndex ? '#f2f5ff' : 'transparent',
                  }}
                >
                  {itemRenderer(v)}
                </div>
              ))}
            </div>
          )}
      </>
    )
  }

  private textareaElement(): HTMLTextAreaElement | null {
    return this.textareaRef.current?.getElement?.() ?? null
  }

  private handleWrapperKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { items } = this.props
    const { typeaheadIsOpen } = this.state
    if (!typeaheadIsOpen) {
      if (e.ctrlKey && e.key === ' ') {
        const el = this.textareaElement()
        this.openTypeahead(el?.selectionStart ?? 0, '')
        e.preventDefault()
      }
      requestAnimationFrame(() => this.updateTypeaheadPosition())
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState(
        (s) => ({
          typeaheadSuggestionIndex: Math.min(
            s.typeaheadSuggestionIndex + 1,
            Math.max(0, items.length - 1)
          ),
        }),
        () => {
          this.updateTypeaheadPosition()
          this.scrollActiveOptionIntoView()
        }
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState(
        (s) => ({ typeaheadSuggestionIndex: Math.max(0, s.typeaheadSuggestionIndex - 1) }),
        () => {
          this.updateTypeaheadPosition()
          this.scrollActiveOptionIntoView()
        }
      )
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      const pick = items[this.state.typeaheadSuggestionIndex] || items[0]
      if (pick) this.handleItemSelected(pick)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      this.closeTypeahead()
    } else {
      requestAnimationFrame(() => {
        const el = this.textareaElement()
        if (!el) return
        this.updateTypeaheadFromCaret(el.value, el.selectionStart ?? el.value.length)
        this.updateTypeaheadPosition()
      })
    }
  }

  private findTrigger(value: string, caret: number) {
    const { triggerToken } = this.props
    if (caret <= 0) return null
    const start = value.lastIndexOf(triggerToken, caret - 1)
    if (start === -1) return null

    const raw = value.slice(start + triggerToken.length, caret)
    if (/\r|\n/.test(raw)) return null

    return { start, query: raw.trim() }
  }

  private openTypeahead(start: number, query: string) {
    this.setState(
      {
        typeaheadIsOpen: true,
        typeaheadStart: start,
        typeaheadQuery: query,
        typeaheadSuggestionIndex: 0,
      },
      () => this.updateTypeaheadPosition()
    )
  }

  private closeTypeahead() {
    this.setState({
      typeaheadIsOpen: false,
      typeaheadStart: -1,
      typeaheadQuery: '',
      typeaheadSuggestionIndex: 0,
    })
  }

  private updateTypeaheadFromCaret(nextValue: string, caret: number) {
    const trig = this.findTrigger(nextValue, caret)
    if (trig) this.openTypeahead(trig.start, trig.query)
    else this.closeTypeahead()
  }

  private updateTypeaheadPosition() {
    const { typeaheadIsOpen } = this.state
    const textarea = this.textareaElement()
    const wrapper = this.wrapperRef.current

    if (!textarea || !typeaheadIsOpen) return

    const caret = textarea.selectionStart ?? textarea.value.length

    const { left, top, lineHeight } = getCaretCoords(textarea, caret, { relativeTo: wrapper })
    this.setState({ typeaheadPositionX: left, typeaheadPositionY: top + lineHeight })
  }

  private handleChange = (nextValue: string) => {
    const { onChange } = this.props
    this.setState({ inputValue: nextValue })
    const el = this.textareaElement()
    const caret = el?.selectionStart ?? nextValue.length
    this.updateTypeaheadFromCaret(nextValue, caret)
    requestAnimationFrame(() => this.updateTypeaheadPosition())
    onChange?.(nextValue)
  }

  private scrollActiveOptionIntoView = () => {
    const menu = this.typeaheadRef.current
    if (!menu) return
    const active = menu.querySelector('[data-active="true"]') as HTMLElement | null
    active?.scrollIntoView({ block: 'nearest' })
  }

  private handleItemSelected(item: ITypeaheadItem) {
    const { inputValue, typeaheadStart } = this.state
    const el = this.textareaElement()
    const prev = inputValue
    const caret = el?.selectionStart ?? prev.length
    const start = typeaheadStart >= 0 ? typeaheadStart : caret
    const token = item.insertText

    const next = prev.slice(0, start) + token + prev.slice(caret)

    const newCaret = start + token.length
    requestAnimationFrame(() => {
      const el2 = this.textareaElement()
      if (el2) {
        el2.focus()
        try {
          el2.setSelectionRange(newCaret, newCaret)
        } catch (e) {
          // ignore
        }
      }
      this.updateTypeaheadPosition()
    })
    this.closeTypeahead()
    this.handleChange(next)
  }
}

function renderItem(item: ITypeaheadItem): React.ReactNode {
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{item.label}</div>
      <div style={{ fontSize: 12, opacity: 0.8 }}>{item.description}</div>
    </div>
  )
}

/**
 * Computes caret coords inside a textarea (relative to its content box)
 */
function getCaretCoords(
  textarea: HTMLTextAreaElement,
  caretIndex: number,
  options: { relativeTo?: HTMLElement | null; place?: 'under' | 'over' } = {}
): { left: number; top: number; lineHeight: number } {
  const cs = getComputedStyle(textarea)
  const taRect = textarea.getBoundingClientRect()

  const div = document.createElement('div')
  const span = document.createElement('span')

  const s = div.style
  s.position = 'fixed'
  s.left = `${taRect.left}px`
  s.top = `${taRect.top}px`
  s.visibility = 'hidden'
  s.pointerEvents = 'none'
  s.whiteSpace = 'pre-wrap'

  s.boxSizing = cs.boxSizing
  s.width = (cs.boxSizing === 'border-box' ? textarea.offsetWidth : textarea.clientWidth) + 'px'

  // Text up to caret; ensure last line renders if it ends with \n
  const before = textarea.value.slice(0, caretIndex)
  div.textContent = before.endsWith('\n') ? before + '\u200b' : before

  span.textContent = '\u200b'
  div.appendChild(span)
  document.body.appendChild(div)

  const caretRect = span.getBoundingClientRect()
  const baseRect = (options.relativeTo ?? textarea).getBoundingClientRect()

  const left = caretRect.left - baseRect.left
  const topEdge = (options.place === 'over' ? caretRect.top : caretRect.bottom) - baseRect.top

  // Fallback for 'normal' line-height
  const lh =
    cs.lineHeight === 'normal'
      ? parseFloat(cs.fontSize) * 1.2
      : parseFloat(cs.lineHeight || cs.fontSize)

  document.body.removeChild(div)
  return { left, top: topEdge, lineHeight: lh }
}
